import { Link, useNavigate } from 'react-router';
import {
  useCreateTemplate,
  useDeleteTemplate,
  useDuplicateTemplate,
  useTemplates,
} from '../../api/queries';
import { EmptyState, ErrorState, PageHeader, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { errorMessage } from '../../lib/api';
import { fileUrl, formatDate, newId } from '../../lib/utils';

export function TemplatesPage() {
  const { data: templates, isPending, error } = useTemplates();
  const create = useCreateTemplate();
  const duplicate = useDuplicateTemplate();
  const remove = useDeleteTemplate();
  const navigate = useNavigate();
  const toast = useToast();

  function handleCreate() {
    create.mutate(
      {
        name: 'Untitled form',
        sections: [{ id: newId('s'), title: 'Section 1', questions: [] }],
      },
      {
        onSuccess: (t) => navigate(`/admin/templates/${t.id}`),
        onError: (err) => toast.error(errorMessage(err)),
      },
    );
  }

  function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete the template “${name}”? Existing client links keep working.`))
      return;
    remove.mutate(id, {
      onSuccess: (r) =>
        toast.success(
          r.archived ? 'Template archived (it is used by client links)' : 'Template deleted',
        ),
      onError: (err) => toast.error(errorMessage(err)),
    });
  }

  return (
    <>
      <PageHeader
        title="Templates"
        subtitle="Reusable forms. Edit questions, add images, then create a personal link for each client."
        actions={
          <button className="btn btn-primary" onClick={handleCreate} disabled={create.isPending}>
            + New template
          </button>
        }
      />

      {isPending && <Spinner />}
      {error && <ErrorState message={errorMessage(error)} />}
      {templates?.length === 0 && (
        <EmptyState title="No templates yet">
          <p className="muted">
            Create one, or run <code>npm run seed</code> to import the Business and Marketing
            Discovery Form.
          </p>
        </EmptyState>
      )}

      <div className="grid-cards">
        {templates?.map((t) => (
          <article key={t.id} className="card template-card">
            <div className="template-card-head">
              {t.logoImageId ? (
                <img src={fileUrl(t.logoImageId)} alt="" className="template-logo" />
              ) : (
                <div className="template-logo placeholder">{t.name.slice(0, 1).toUpperCase()}</div>
              )}
              <div>
                <h3>
                  <Link to={`/admin/templates/${t.id}`}>{t.name}</Link>
                </h3>
                <p className="muted small">
                  {t.sectionCount} sections · {t.questionCount} questions · {t.linkCount} client
                  links
                </p>
              </div>
            </div>
            {t.description && <p className="clamp-3">{t.description}</p>}
            <p className="muted small">Updated {formatDate(t.updatedAt)}</p>
            <div className="row wrap">
              <Link className="btn btn-primary btn-sm" to={`/admin/links?new=${t.id}`}>
                Create client link
              </Link>
              <Link className="btn btn-secondary btn-sm" to={`/admin/templates/${t.id}`}>
                Edit
              </Link>
              <Link className="btn btn-ghost btn-sm" to={`/admin/templates/${t.id}/preview`}>
                Preview
              </Link>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  duplicate.mutate(t.id, {
                    onSuccess: () => toast.success('Template duplicated'),
                    onError: (err) => toast.error(errorMessage(err)),
                  })
                }
              >
                Duplicate
              </button>
              <button
                className="btn btn-ghost btn-sm danger"
                onClick={() => handleDelete(t.id, t.name)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
